import { useCallback, useEffect, useRef, useState } from 'react'

export const DRAFT_SAVE_DELAY_MS = 800
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Keeps a browser draft of a form, so a closed tab or a crash loses nothing.
 *
 * Pass a storage `key` that is already known on the first render (null turns it
 * off), the form's values as one plain object `state`, and a `version` to bump
 * when the shape of `state` changes: an older draft is then dropped, not restored.
 *
 * - A draft younger than 7 days is offered back as `draft` ({ savedAt, state }).
 *   An older or unreadable one is removed without a word.
 * - Nothing is saved while `draft` is waiting, so the blank form cannot overwrite it.
 * - `state` is saved 800 ms after the last change. A state equal to the one the
 *   form started with removes the draft instead: a reset form has nothing to keep.
 * - Storage that is blocked or full is logged and ignored. The form still works.
 *
 * Returns { draft, restoreDraft, clearDraft }. `restoreDraft()`
 * returns the saved state for the caller to apply. `clearDraft()` removes it, for
 * Discard and for a successful save.
 */
export function useFormAutosave(key, state, { version }) {
  const serialised = JSON.stringify(state)
  const startedWith = useRef(serialised)
  const latestState = useRef(state)
  latestState.current = state

  const [draft, setDraft] = useState(() => (key ? loadDraft(key, version) : null))

  useEffect(() => {
    if (!key || draft) return undefined
    const timer = setTimeout(() => {
      if (serialised === startedWith.current) {
        removeStored(key)
      } else {
        storeDraft(key, { version, savedAt: Date.now(), state: latestState.current })
      }
    }, DRAFT_SAVE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [key, version, draft, serialised])

  const restoreDraft = useCallback(() => {
    const saved = draft.state
    setDraft(null)
    return saved
  }, [draft])

  const clearDraft = useCallback(() => {
    if (key) removeStored(key)
    setDraft(null)
  }, [key])

  return { draft, restoreDraft, clearDraft }
}

function loadDraft(key, version) {
  const stored = readStored(key)
  if (!stored) return null
  const fresh = Date.now() - stored.savedAt < DRAFT_MAX_AGE_MS
  if (stored.version === version && fresh && stored.state) return stored
  removeStored(key)
  return null
}

function readStored(key) {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch (err) {
    console.warn('Draft could not be read:', err)
    return null
  }
}

function storeDraft(key, draft) {
  try {
    window.localStorage.setItem(key, JSON.stringify(draft))
  } catch (err) {
    console.warn('Draft could not be saved:', err)
  }
}

function removeStored(key) {
  try {
    window.localStorage.removeItem(key)
  } catch (err) {
    console.warn('Draft could not be removed:', err)
  }
}
